import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Network, Download, RefreshCw, X,
  ZoomIn, ZoomOut, Maximize2,
} from 'lucide-react'
import {
  Button,
  Card,
  EmptyState,
  IconButton,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
} from '@/components/ui'

// ---- Types ----

interface NetworkDevice {
  id: string
  name: string
  model?: string
  mac?: string
  ip?: string
  status: 'online' | 'offline'
  type?: string
  firmware?: string
  uplinkDeviceId?: string
  uplinkType?: string
}

interface Site {
  id: string
  name: string
}

interface GraphNode {
  id: string
  label: string
  type: 'router' | 'switch' | 'ap' | 'client'
  status: 'online' | 'offline' | 'warning'
  x: number
  y: number
  vx: number
  vy: number
  device: NetworkDevice
}

interface GraphEdge {
  source: string
  target: string
  type: 'uplink' | 'wlan'
}

// ---- Helper: classify device type ----

function classifyDevice(d: NetworkDevice): GraphNode['type'] {
  const t = (d.type || '').toLowerCase()
  const model = (d.model || '').toLowerCase()
  const name = (d.name || '').toLowerCase()

  if (t.includes('ugw') || t.includes('gateway') || t.includes('router') || model.includes('gateway') || model.includes('udm') || name.includes('router') || name.includes('gateway')) {
    return 'router'
  }
  if (t.includes('usw') || t.includes('switch') || model.includes('switch') || model.includes('usw') || name.includes('switch')) {
    return 'switch'
  }
  if (t.includes('uap') || t.includes('ap') || model.includes('uap') || model.includes('u6') || model.includes('access') || name.includes('ap') || t.includes('wifi')) {
    return 'ap'
  }
  return 'client'
}

// ---- Helper: status color ----

function statusColor(status: GraphNode['status']): string {
  switch (status) {
    case 'online': return '#22c55e'
    case 'offline': return '#ef4444'
    case 'warning': return '#eab308'
  }
}

function statusBg(status: GraphNode['status']): string {
  switch (status) {
    case 'online': return '#dcfce7'
    case 'offline': return '#fee2e2'
    case 'warning': return '#fef9c3'
  }
}

// ---- Force-directed layout simulation ----

const FORCE_CONFIG = {
  repulsion: 3000,
  attraction: 0.005,
  damping: 0.85,
  centerGravity: 0.01,
  maxVelocity: 8,
  idealEdgeLength: 150,
}

function simulateForces(
  nodes: GraphNode[],
  edges: GraphEdge[],
  width: number,
  height: number,
): void {
  const cx = width / 2
  const cy = height / 2

  // Repulsion between all node pairs
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i]
      const b = nodes[j]
      let dx = a.x - b.x
      let dy = a.y - b.y
      let dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < 1) { dx = Math.random() - 0.5; dy = Math.random() - 0.5; dist = 1 }
      const force = FORCE_CONFIG.repulsion / (dist * dist)
      const fx = (dx / dist) * force
      const fy = (dy / dist) * force
      a.vx += fx
      a.vy += fy
      b.vx -= fx
      b.vy -= fy
    }
  }

  // Attraction along edges
  for (const edge of edges) {
    const a = nodes.find((n) => n.id === edge.source)
    const b = nodes.find((n) => n.id === edge.target)
    if (!a || !b) continue
    const dx = b.x - a.x
    const dy = b.y - a.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist < 1) continue
    const force = (dist - FORCE_CONFIG.idealEdgeLength) * FORCE_CONFIG.attraction
    const fx = (dx / dist) * force
    const fy = (dy / dist) * force
    a.vx += fx
    a.vy += fy
    b.vx -= fx
    b.vy -= fy
  }

  // Center gravity
  for (const node of nodes) {
    node.vx += (cx - node.x) * FORCE_CONFIG.centerGravity
    node.vy += (cy - node.y) * FORCE_CONFIG.centerGravity
  }

  // Apply velocity with damping and clamp
  for (const node of nodes) {
    node.vx *= FORCE_CONFIG.damping
    node.vy *= FORCE_CONFIG.damping
    const speed = Math.sqrt(node.vx * node.vx + node.vy * node.vy)
    if (speed > FORCE_CONFIG.maxVelocity) {
      node.vx = (node.vx / speed) * FORCE_CONFIG.maxVelocity
      node.vy = (node.vy / speed) * FORCE_CONFIG.maxVelocity
    }
    node.x += node.vx
    node.y += node.vy
    // Keep within bounds
    node.x = Math.max(60, Math.min(width - 60, node.x))
    node.y = Math.max(60, Math.min(height - 60, node.y))
  }
}

// ---- Build graph from devices ----

function buildGraph(devices: NetworkDevice[], width: number, height: number): { nodes: GraphNode[]; edges: GraphEdge[] } {
  const nodes: GraphNode[] = devices.map((d, i) => ({
    id: d.id,
    label: d.name,
    type: classifyDevice(d),
    status: d.status === 'online' ? 'online' : 'offline',
    x: width / 2 + (Math.cos((i / devices.length) * Math.PI * 2) * 200) + (Math.random() - 0.5) * 100,
    y: height / 2 + (Math.sin((i / devices.length) * Math.PI * 2) * 200) + (Math.random() - 0.5) * 100,
    vx: 0,
    vy: 0,
    device: d,
  }))

  const edges: GraphEdge[] = []
  const nodeIds = new Set(nodes.map((n) => n.id))

  for (const d of devices) {
    if (d.uplinkDeviceId && nodeIds.has(d.uplinkDeviceId)) {
      edges.push({
        source: d.id,
        target: d.uplinkDeviceId,
        type: classifyDevice(d) === 'ap' ? 'wlan' : 'uplink',
      })
    }
  }

  // If no edges at all, create a star topology from first router/switch
  if (edges.length === 0 && nodes.length > 1) {
    const center = nodes.find((n) => n.type === 'router') || nodes.find((n) => n.type === 'switch') || nodes[0]
    for (const n of nodes) {
      if (n.id !== center.id) {
        edges.push({ source: n.id, target: center.id, type: n.type === 'ap' ? 'wlan' : 'uplink' })
      }
    }
  }

  return { nodes, edges }
}

// ---- SVG Node Shapes ----

function NodeShape({ node, isSelected, onClick }: { node: GraphNode; isSelected: boolean; onClick: () => void }) {
  const color = statusColor(node.status)
  const bg = statusBg(node.status)
  const strokeWidth = isSelected ? 3 : 1.5
  const selectedStroke = isSelected ? '#3b82f6' : color

  switch (node.type) {
    case 'router':
      // Diamond
      return (
        <g onClick={onClick} style={{ cursor: 'pointer' }}>
          <polygon
            points={`${node.x},${node.y - 22} ${node.x + 22},${node.y} ${node.x},${node.y + 22} ${node.x - 22},${node.y}`}
            fill={bg}
            stroke={selectedStroke}
            strokeWidth={strokeWidth}
          />
          <text x={node.x} y={node.y + 36} textAnchor="middle" fontSize={11} fill="#374151" fontWeight={500}>
            {node.label.length > 18 ? node.label.slice(0, 16) + '...' : node.label}
          </text>
        </g>
      )
    case 'switch':
      // Rectangle
      return (
        <g onClick={onClick} style={{ cursor: 'pointer' }}>
          <rect
            x={node.x - 24}
            y={node.y - 16}
            width={48}
            height={32}
            rx={4}
            fill={bg}
            stroke={selectedStroke}
            strokeWidth={strokeWidth}
          />
          <text x={node.x} y={node.y + 36} textAnchor="middle" fontSize={11} fill="#374151" fontWeight={500}>
            {node.label.length > 18 ? node.label.slice(0, 16) + '...' : node.label}
          </text>
        </g>
      )
    case 'ap':
      // Circle
      return (
        <g onClick={onClick} style={{ cursor: 'pointer' }}>
          <circle
            cx={node.x}
            cy={node.y}
            r={18}
            fill={bg}
            stroke={selectedStroke}
            strokeWidth={strokeWidth}
          />
          {/* Wifi icon indicator */}
          <circle cx={node.x} cy={node.y - 3} r={3} fill={color} />
          <path
            d={`M${node.x - 6},${node.y + 4} A8,8 0 0,1 ${node.x + 6},${node.y + 4}`}
            fill="none" stroke={color} strokeWidth={1.5}
          />
          <text x={node.x} y={node.y + 34} textAnchor="middle" fontSize={11} fill="#374151" fontWeight={500}>
            {node.label.length > 18 ? node.label.slice(0, 16) + '...' : node.label}
          </text>
        </g>
      )
    default:
      // Client - small circle
      return (
        <g onClick={onClick} style={{ cursor: 'pointer' }}>
          <circle
            cx={node.x}
            cy={node.y}
            r={12}
            fill={bg}
            stroke={selectedStroke}
            strokeWidth={strokeWidth}
          />
          <text x={node.x} y={node.y + 28} textAnchor="middle" fontSize={10} fill="#6b7280">
            {node.label.length > 16 ? node.label.slice(0, 14) + '...' : node.label}
          </text>
        </g>
      )
  }
}

// ---- Detail Panel ----

function DeviceDetailPanel({ node, onClose }: { node: GraphNode; onClose: () => void }) {
  const d = node.device
  const rows: Array<[string, React.ReactNode]> = [
    ['Typ', <span className="capitalize">{node.type}</span>],
  ]
  if (d.ip) rows.push(['IP-Adresse', <span className="font-mono text-caption">{d.ip}</span>])
  if (d.mac) rows.push(['MAC-Adresse', <span className="font-mono text-caption">{d.mac}</span>])
  if (d.model) rows.push(['Modell', <span>{d.model}</span>])
  if (d.firmware) rows.push(['Firmware', <span className="font-mono text-caption">{d.firmware}</span>])

  return (
    <Card className="absolute right-4 top-4 z-overlay w-80 shadow-popover">
      <Card.Header>
        <Card.Title className="truncate">{d.name}</Card.Title>
        <IconButton
          icon={<X />}
          aria-label="Schließen"
          size="sm"
          onClick={onClose}
        />
      </Card.Header>
      <Card.Body className="space-y-2.5">
        <div className="flex items-center justify-between text-small">
          <span className="text-gray-500">Status</span>
          <StatusBadge status={d.status} size="sm" />
        </div>
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between text-small">
            <span className="text-gray-500">{label}</span>
            <span className="text-gray-900">{value}</span>
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}

// ---- Main Component ----

export function NetworkVisualizationPage() {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>(0)
  const nodesRef = useRef<GraphNode[]>([])
  const edgesRef = useRef<GraphEdge[]>([])

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [, setTick] = useState(0) // force re-render
  const [dimensions, setDimensions] = useState({ width: 900, height: 600 })
  const [dragNodeId, setDragNodeId] = useState<string | null>(null)
  const [simulationRunning, setSimulationRunning] = useState(false)
  const iterationRef = useRef(0)

  // Fetch sites
  const { data: sites = [] } = useQuery<Site[]>({
    queryKey: ['network', 'sites'],
    queryFn: () => apiClient.get('/network/sites').then((r) => r.data),
  })

  // Auto-select first site
  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id)
    }
  }, [sites, selectedSiteId])

  // Fetch devices for selected site
  const { data: devices = [], isLoading: devicesLoading } = useQuery<NetworkDevice[]>({
    queryKey: ['network', 'devices', selectedSiteId],
    queryFn: () => apiClient.get(`/network/sites/${selectedSiteId}/devices`).then((r) => r.data),
    enabled: !!selectedSiteId,
    refetchInterval: 60_000,
  })

  // Measure container
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const obs = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setDimensions({ width, height })
        }
      }
    })
    obs.observe(container)
    return () => obs.disconnect()
  }, [])

  // Build graph when devices change
  useEffect(() => {
    if (devices.length === 0) {
      nodesRef.current = []
      edgesRef.current = []
      setTick((t) => t + 1)
      return
    }
    const { nodes, edges } = buildGraph(devices, dimensions.width, dimensions.height)
    nodesRef.current = nodes
    edgesRef.current = edges
    iterationRef.current = 0
    setSimulationRunning(true)
    setTick((t) => t + 1)
  }, [devices, dimensions.width, dimensions.height])

  // Force simulation loop
  useEffect(() => {
    if (!simulationRunning) return
    const MAX_ITERATIONS = 200

    const step = () => {
      if (iterationRef.current >= MAX_ITERATIONS) {
        setSimulationRunning(false)
        return
      }
      simulateForces(nodesRef.current, edgesRef.current, dimensions.width, dimensions.height)
      iterationRef.current++
      setTick((t) => t + 1)
      animationRef.current = requestAnimationFrame(step)
    }
    animationRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(animationRef.current)
  }, [simulationRunning, dimensions.width, dimensions.height])

  // Drag handlers
  const handleMouseDown = useCallback((nodeId: string) => {
    setDragNodeId(nodeId)
  }, [])

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragNodeId || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / zoom
    const y = (e.clientY - rect.top) / zoom
    const node = nodesRef.current.find((n) => n.id === dragNodeId)
    if (node) {
      node.x = x
      node.y = y
      node.vx = 0
      node.vy = 0
      setTick((t) => t + 1)
    }
  }, [dragNodeId, zoom])

  const handleMouseUp = useCallback(() => {
    setDragNodeId(null)
  }, [])

  // Export PNG
  const handleExportPng = useCallback(() => {
    const svg = svgRef.current
    if (!svg) return
    const serializer = new XMLSerializer()
    const svgStr = serializer.serializeToString(svg)
    const canvas = document.createElement('canvas')
    canvas.width = dimensions.width * 2
    canvas.height = dimensions.height * 2
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const img = new Image()
    const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    img.onload = () => {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(url)
      const dataUrl = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.download = `netzwerk-topologie-${selectedSiteId || 'export'}.png`
      link.href = dataUrl
      link.click()
    }
    img.src = url
  }, [dimensions, selectedSiteId])

  const selectedNode = useMemo(
    () => nodesRef.current.find((n) => n.id === selectedNodeId) || null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedNodeId, nodesRef.current],
  )

  const nodes = nodesRef.current
  const edges = edgesRef.current

  const legend = [
    { type: 'Router', shape: 'diamond', color: '#22c55e' },
    { type: 'Switch', shape: 'rect', color: '#22c55e' },
    { type: 'Access Point', shape: 'circle', color: '#22c55e' },
    { type: 'Client', shape: 'small-circle', color: '#22c55e' },
  ]

  const siteOptions = sites.map((s) => ({ value: s.id, label: s.name }))

  return (
    <div className="flex h-full flex-col space-y-4">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <Network className="h-6 w-6 text-brand-500" />
            Netzwerkvisualisierung
          </span>
        }
        description="Topologie-Graph der Geräte aus der ausgewählten UniFi-Site."
        actions={
          <>
            {siteOptions.length > 0 && (
              <Select
                value={selectedSiteId ?? undefined}
                onValueChange={(v) => {
                  setSelectedSiteId(v)
                  setSelectedNodeId(null)
                }}
                options={siteOptions}
                placeholder="Site wählen …"
                triggerClassName="min-w-[12rem]"
              />
            )}
            <IconButton
              icon={<RefreshCw />}
              aria-label="Layout neu berechnen"
              onClick={() => {
                iterationRef.current = 0
                setSimulationRunning(true)
              }}
              title="Layout neu berechnen"
            />
            <IconButton
              icon={<ZoomIn />}
              aria-label="Hineinzoomen"
              onClick={() => setZoom((z) => Math.min(z + 0.2, 3))}
              title="Hineinzoomen"
            />
            <IconButton
              icon={<ZoomOut />}
              aria-label="Herauszoomen"
              onClick={() => setZoom((z) => Math.max(z - 0.2, 0.3))}
              title="Herauszoomen"
            />
            <IconButton
              icon={<Maximize2 />}
              aria-label="Zoom zurücksetzen"
              onClick={() => setZoom(1)}
              title="Zoom zurücksetzen"
            />
            <Button variant="primary" leadingIcon={<Download className="h-4 w-4" />} onClick={handleExportPng}>
              PNG Export
            </Button>
          </>
        }
      />

      {/* Legend */}
      <div className="flex items-center gap-4 text-caption text-gray-500">
        {legend.map((l) => (
          <div key={l.type} className="flex items-center gap-1.5">
            {l.shape === 'diamond' && (
              <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="7,1 13,7 7,13 1,7" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" /></svg>
            )}
            {l.shape === 'rect' && (
              <svg width="16" height="12" viewBox="0 0 16 12"><rect x="1" y="1" width="14" height="10" rx="2" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" /></svg>
            )}
            {l.shape === 'circle' && (
              <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="6" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" /></svg>
            )}
            {l.shape === 'small-circle' && (
              <svg width="12" height="12" viewBox="0 0 12 12"><circle cx="6" cy="6" r="5" fill="#dcfce7" stroke="#22c55e" strokeWidth="1.5" /></svg>
            )}
            <span>{l.type}</span>
          </div>
        ))}
        <div className="ml-4 flex items-center gap-3 border-l border-gray-200 pl-4">
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-green-500" /> Online</div>
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Offline</div>
          <div className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-yellow-500" /> Warnung</div>
        </div>
      </div>

      {/* Graph Canvas */}
      <Card
        ref={containerRef}
        className="relative flex-1 overflow-hidden"
        style={{ minHeight: 500 }}
      >
        {devicesLoading && (
          <div className="absolute inset-0 z-overlay flex items-center justify-center bg-white/80">
            <LoadingState label="Lade Geräte …" />
          </div>
        )}

        {!devicesLoading && devices.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <EmptyState
              icon={<Network />}
              title="Keine Geräte gefunden"
              description="Bitte eine Site mit Geräten wählen."
            />
          </div>
        )}

        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
          className="h-full w-full"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Edges */}
          {edges.map((edge, i) => {
            const src = nodes.find((n) => n.id === edge.source)
            const tgt = nodes.find((n) => n.id === edge.target)
            if (!src || !tgt) return null
            return (
              <line
                key={`edge-${i}`}
                x1={src.x}
                y1={src.y}
                x2={tgt.x}
                y2={tgt.y}
                stroke={edge.type === 'wlan' ? '#93c5fd' : '#d1d5db'}
                strokeWidth={edge.type === 'wlan' ? 1.5 : 2}
                strokeDasharray={edge.type === 'wlan' ? '4,4' : undefined}
              />
            )
          })}

          {/* Nodes */}
          {nodes.map((node) => (
            <g
              key={node.id}
              onMouseDown={(e) => { e.preventDefault(); handleMouseDown(node.id) }}
            >
              <NodeShape
                node={node}
                isSelected={selectedNodeId === node.id}
                onClick={() => setSelectedNodeId(selectedNodeId === node.id ? null : node.id)}
              />
            </g>
          ))}
        </svg>

        {/* Detail Panel */}
        {selectedNode && (
          <DeviceDetailPanel
            node={selectedNode}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
      </Card>
    </div>
  )
}
